import React, {useEffect, useState} from 'react';
import ENDPOINTS from '../config/apiEndPoints';
import LoadingSpinner from "./LoadingSpinner";
import ElectionChart from "./ElectionChart";
import ElectionResultsTable from './ElectionResultsTable';
import ActionButtons from "./ActionButtons";
import {getUrlParam, openPopup} from "../config/windowUtils";
import MenuButton from "./MenuButtons";
import {shareComponent, ShareModal} from "../config/shareUtils";

// Fallback data in case the API fails
const FALLBACK_DATA = [
  { name: 'CORPORATION', total: 6, ldf: 0, udf: 0, nda: 0, hung: 0, party_20_20: 0 },
  { name: 'DIST. PANCHAYAT', total: 14, ldf: 0, udf: 0, nda: 0, hung: 0, party_20_20: 0 },
  { name: 'MUNICIPALITY', total: 86, ldf: 0, udf: 0, nda: 0, hung: 0, party_20_20: 0 },
  { name: 'BLOCK', total: 152, ldf: 0, udf: 0, nda: 0, hung: 0, party_20_20: 0 },
  { name: 'GRAM PANCHAYAT', total: 941, ldf: 0, udf: 0, nda: 0, hung: 0, party_20_20: 2 },
];

const DistrictWise = () => {
  const [activeTab, setActiveTab] = useState('2025');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(() => {
    return localStorage.getItem('selectedDistrict') || 'Trivandrum';
  });
  // const [selectedDistrict, setSelectedDistrict] = useState('Trivandrum');
  const [currentDistrictEndpoint, setCurrentDistrictEndpoint] = useState(ENDPOINTS.DISTRICT_WISE_2025);
  const [isLoading, setIsLoading] = useState(false);
  const [shareState, setShareState] = useState(null);

  useEffect(() => {
    localStorage.setItem('selectedDistrict', selectedDistrict || 'Trivandrum');
  }, [selectedDistrict]);

  const handleShare = async () => {
    const result = await shareComponent('Statewide Results', `${window.location.origin}${window.location.pathname}?show=districts`);
    setShareState(result);
  };

  const keralaDistricts = [
    { value: 'Trivandrum', label: 'TRIVANDRUM' },
    { value: 'Kollam', label: 'KOLLAM' },
    { value: 'Pathanamthitta', label: 'PATHANAMTHITTA' },
    { value: 'Alappuzha', label: 'ALAPPUZHA' },
    { value: 'Kottayam', label: 'KOTTAYAM' },
    { value: 'Idukki', label: 'IDUKKI' },
    { value: 'Ernakulam', label: 'ERNAKULAM' },
    { value: 'Thrissur', label: 'THRISSUR' },
    { value: 'Palakkad', label: 'PALAKKAD' },
    { value: 'Malappuram', label: 'MALAPPURAM' },
    { value: 'Kozhikode', label: 'KOZHIKODE' },
    { value: 'Wayanad', label: 'WAYANAD' },
    { value: 'Kannur', label: 'KANNUR' },
    { value: 'Kasargod', label: 'KASARAGOD' }
  ];

  const handleDistrictSelect = async (district, e) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      setIsLoading(true);
      const endpointTemplate = activeTab === '2025'
        ? ENDPOINTS.DISTRICT_WISE_2025.replace('/Trivandrum', `/${district}`)
        : ENDPOINTS.DISTRICT_WISE_2020.replace('/Trivandrum', `/${district}`);

      setCurrentDistrictEndpoint(endpointTemplate);
      setSelectedDistrict(district);
      setIsDropdownOpen(false);

      const now = new Date();
      const timestamp = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;

      const response = await fetch(`${endpointTemplate}?ver=${timestamp}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || 'Failed to fetch district data';
        console.error('API Error:', errorMessage);
        // setError(errorMessage); // Uncomment if you have an error state
        return; // Exit the function early on error
      }
      await response.json();
    } catch (error) {
      console.error('Error fetching district data:', error);
    } finally {
      setIsLoading(false);
    }
  };

// Update endpoint when tab changes
  useEffect(() => {
    let timer;

    const updateEndpoint = async () => {
      setIsLoading(true);
      try {
        const endpoint = activeTab === '2025'
          ? ENDPOINTS.DISTRICT_WISE_2025.replace('/Trivandrum', `/${selectedDistrict}`)
          : ENDPOINTS.DISTRICT_WISE_2020.replace('/Trivandrum', `/${selectedDistrict}`);

        setCurrentDistrictEndpoint(endpoint);
      } catch (error) {
        console.error('Error updating endpoint:', error);
      } finally {
        timer = setTimeout(() => {
          setIsLoading(false);
        }, 300);
      }
    };
    updateEndpoint().then();
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [activeTab, selectedDistrict]);

  const seriesConfig = [
    { name: 'LDF', dataKey: 'ldf', color: '#ED423D' },
    { name: 'UDF', dataKey: 'udf', color: '#5C93C7' },
    { name: 'NDA', dataKey: 'nda', color: '#FC9E35' },
    { name: 'HUNG', dataKey: 'hung', color: '#6B4E9B' },
    { name: 'OTH', dataKey: 'oth', color: '#787878' }
  ];

  const plotOptions = {
    column: {
      stacking: 'normal',
      pointWidth: 45
    }
  };

  const handlePartyWise = () => {
    if (!selectedDistrict) {
      console.error('No district selected');
      return;
    }
    const url = `${window.location.origin}${window.location.pathname}?show=parties&list=${selectedDistrict}`;
    openPopup(url, 700);
  };
  const handleMapView = () => {
    const url = `https://results.mathrubhumi.com/result/localbody-mapview/?page=${selectedDistrict}`;
    openPopup(url, 1000, 1000);
  };

  const showControls = !getUrlParam('show');

  return (
    <>
      {!showControls && (
        <div className="allianceHeader">DISTRICT-WISE RESULTS: KERALA PANCHAYAT ELECTIONS 2025</div>
      )}
      <div className="election-results-inner" id="districtWise">
        <div className="icons">
          {showControls && (
            <MenuButton
              menuItems={[
                {
                  label: 'FULL RESULT',
                  onClick: () => openPopup('https://results.mathrubhumi.com/result/panchayat-2025/full-result.html', 800)
                },
                {
                  label: 'MAP VIEW',
                  onClick: () => openPopup(`https://results.mathrubhumi.com/result/localbody-mapview/?page=${selectedDistrict}`, 1000)
                },
                {
                  label: 'PARTY-WISE',
                  onClick: () => openPopup(`${window.location.origin}${window.location.pathname}?show=parties&list=${selectedDistrict}`, 700)
                }
              ]}
            >
              <svg className="menu-icon" xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                <path
                  d="M2.83331 4.25H14.1666M2.83331 8.5H14.1666M2.83331 12.75H14.1666"
                  stroke="#403E3E"
                  strokeWidth="1.41667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </MenuButton>
          )}
          <div className="icons-r">
            <div className="dropdown dropdown-1" onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setIsDropdownOpen(false);
              }
            }}>
              <button
                className="dropdown-toggle dropdown-toggle-1"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
                aria-label="Select district"
              >
                {keralaDistricts.find(d => d.value === selectedDistrict)?.label || 'Select District'}
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M1 1.5L6 6.5L11 1.5" stroke="#403E3E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {isDropdownOpen && (
                <div
                  className="dropdown-menu"
                  role="listbox"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {keralaDistricts.map((district) => (
                    <div
                      key={district.value}
                      role="option"
                      aria-selected={selectedDistrict === district.value}
                      className={`dropdown-item ${selectedDistrict === district.value ? 'active' : ''}`}
                      onClick={(e) => handleDistrictSelect(district.value, e)}
                    >
                      {district.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {showControls && (
              <svg
                className="open-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                onClick={(e) => {
                  e.preventDefault();
                  openPopup(`${window.location.origin}${window.location.pathname}?show=districts`, 525, 900);
                }}
                style={{ cursor: 'pointer' }}
                role="button"
                aria-label="Open district-wise in new tab"
              >
                <path d="M0 4.85658C0 5.30531 0.363772 5.66908 0.8125 5.66908C1.26123 5.66908 1.625 5.30531 1.625 4.85658H0ZM1.625 0.8125C1.625 0.363772 1.26123 0 0.8125 0C0.363772 0 0 0.363772 0 0.8125H1.625ZM0.8125 0C0.363772 0 0 0.363772 0 0.8125C0 1.26123 0.363772 1.625 0.8125 1.625V0ZM4.85658 1.625C5.30531 1.625 5.66908 1.26123 5.66908 0.8125C5.66908 0.363772 5.30531 0 4.85658 0V1.625ZM1.38702 0.237976C1.06973 -0.0793219 0.555273 -0.0793219 0.237976 0.237976C-0.0793219 0.555273 -0.0793219 1.06973 0.237976 1.38702L1.38702 0.237976ZM5.65464 6.80366C5.97198 7.12097 6.48635 7.12097 6.80366 6.80366C7.12097 6.48635 7.12097 5.97198 6.80366 5.65464L5.65464 6.80366ZM11.9351 15.1667C11.4864 15.1667 11.1226 15.5304 11.1226 15.9792C11.1226 16.4279 11.4864 16.7917 11.9351 16.7917V15.1667ZM15.9792 16.7917C16.4279 16.7917 16.7917 16.4279 16.7917 15.9792C16.7917 15.5304 16.4279 15.1667 15.9792 15.1667V16.7917ZM15.1667 15.9792C15.1667 16.4279 15.5304 16.7917 15.9792 16.7917C16.4279 16.7917 16.7917 16.4279 16.7917 15.9792H15.1667ZM16.7917 11.9351C16.7917 11.4864 16.4279 11.1226 15.9792 11.1226C15.5304 11.1226 15.1667 11.4864 15.1667 11.9351H16.7917ZM15.4047 16.5537C15.722 16.871 16.2363 16.871 16.5537 16.5537C16.871 16.2363 16.871 15.722 16.5537 15.4047L15.4047 16.5537ZM11.137 9.98801C10.8197 9.6707 10.3053 9.6707 9.98801 9.98801C9.6707 10.3053 9.6707 10.8197 9.98801 11.137L11.137 9.98801ZM1.625 11.9351C1.625 11.4864 1.26123 11.1226 0.8125 11.1226C0.363772 11.1226 0 11.4864 0 11.9351H1.625ZM0 15.9792C0 16.4279 0.363772 16.7917 0.8125 16.7917C1.26123 16.7917 1.625 16.4279 1.625 15.9792H0ZM0.8125 15.1667C0.363772 15.1667 0 15.5304 0 15.9792C0 16.4279 0.363772 16.7917 0.8125 16.7917V15.1667ZM4.85658 16.7917C5.30531 16.7917 5.66908 16.4279 5.66908 15.9792C5.66908 15.5304 5.30531 15.1667 4.85658 15.1667V16.7917ZM0.237976 15.4047C-0.0793219 15.722 -0.0793219 16.2363 0.237976 16.5537C0.555273 16.871 1.06973 16.871 1.38702 16.5537L0.237976 15.4047ZM6.80366 11.137C7.12097 10.8197 7.12097 10.3053 6.80366 9.98801C6.48635 9.6707 5.97198 9.6707 5.65464 9.98801L6.80366 11.137ZM11.9351 0C11.4864 0 11.1226 0.363772 11.1226 0.8125C11.1226 1.26123 11.4864 1.625 11.9351 1.625V0ZM15.9792 1.625C16.4279 1.625 16.7917 1.26123 16.7917 0.8125C16.7917 0.363772 16.4279 0 15.9792 0V1.625ZM16.7917 0.8125C16.7917 0.363772 16.4279 0 15.9792 0C15.5304 0 15.1667 0.363772 15.1667 0.8125H16.7917ZM15.1667 4.85658C15.1667 5.30531 15.5304 5.66908 15.9792 5.66908C16.4279 5.66908 16.7917 5.30531 16.7917 4.85658H15.1667ZM16.5537 1.38702C16.871 1.06973 16.871 0.555273 16.5537 0.237976C16.2363 -0.0793219 15.722 -0.0793219 15.4047 0.237976L16.5537 1.38702ZM9.98801 5.65464C9.6707 5.97198 9.6707 6.48635 9.98801 6.80366C10.3053 7.12097 10.8197 7.12097 11.137 6.80366L9.98801 5.65464ZM1.625 4.85658V0.8125H0V4.85658H1.625ZM0.8125 1.625H4.85658V0H0.8125V1.625ZM0.237976 1.38702L5.65464 6.80366L6.80366 5.65464L1.38702 0.237976L0.237976 1.38702ZM11.9351 16.7917H15.9792V15.1667H11.9351V16.7917ZM16.7917 15.9792V11.9351H15.1667V15.9792H16.7917ZM16.5537 15.4047L11.137 9.98801L9.98801 11.137L15.4047 16.5537L16.5537 15.4047ZM0 11.9351V15.9792H1.625V11.9351H0ZM0.8125 16.7917H4.85658V15.1667H0.8125V16.7917ZM1.38702 16.5537L6.80366 11.137L5.65464 9.98801L0.237976 15.4047L1.38702 16.5537ZM11.9351 1.625H15.9792V0H11.9351V1.625ZM15.1667 0.8125V4.85658H16.7917V0.8125H15.1667ZM15.4047 0.237976L9.98801 5.65464L11.137 6.80366L16.5537 1.38702L15.4047 0.237976Z" fill="#403E3E"/>
              </svg>
            )}
            <svg
              className="share-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              onClick={handleShare}
              style={{ cursor: 'pointer' }}
              role="button"
              aria-label="Share District-wise Results"
            >
              <g clipPath="url(#clip0_644_1392)">
                <path fillRule="evenodd" clipRule="evenodd" d="M14.7371 0.599674C13.8626 -0.547357 12.007 0.0610694 12.007 1.49485V2.93454C9.17783 2.90105 7.24249 3.87355 5.95335 5.23165C4.57526 6.68347 4.00844 8.49855 3.77091 9.73965C3.65216 10.3601 4.05917 10.8103 4.48693 10.9658C4.89557 11.1143 5.44162 11.0507 5.80602 10.6342C6.84027 9.45202 8.84963 7.8021 12.007 7.9098V9.75517C12.007 11.1889 13.8626 11.7973 14.7371 10.6503L17.545 6.96775C18.1517 6.17205 18.1517 5.07795 17.545 4.28224L14.7371 0.599674ZM5.61151 8.70712C6.96541 7.52557 8.90888 6.41676 12.007 6.41676H12.7655C13.1844 6.41676 13.5239 6.75077 13.5239 7.16278L13.5238 9.75517L16.3316 6.07258C16.5338 5.80735 16.5338 5.44265 16.3316 5.17741L13.5238 1.49485V3.68053C13.5238 4.09252 13.1843 4.42651 12.7654 4.42651H12.007C8.59448 4.42651 7.06197 6.24985 7.06197 6.24985C6.35643 6.99313 5.90238 7.8765 5.61151 8.70712Z" fill="#403E3E"/>
                <path d="M5.25 0.75293H3C1.75736 0.75293 0.75 1.76028 0.75 3.00293V15.0029C0.75 16.2456 1.75736 17.2529 3 17.2529H15C16.2427 17.2529 17.25 16.2456 17.25 15.0029V12.7529C17.25 12.3387 16.9142 12.0029 16.5 12.0029C16.0858 12.0029 15.75 12.3387 15.75 12.7529V15.0029C15.75 15.4171 15.4142 15.7529 15 15.7529H3C2.58579 15.7529 2.25 15.4171 2.25 15.0029V3.00293C2.25 2.58871 2.58579 2.25293 3 2.25293H5.25C5.66421 2.25293 6 1.91714 6 1.50293C6 1.08871 5.66421 0.75293 5.25 0.75293Z" fill="#403E3E"/>
              </g>
              <defs>
                <clipPath id="clip0_644_1392">
                  <rect width="18" height="18" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </div>
        </div>
        <div className="tab-view tab-view-district">
          <div className="tabs">
            <ul>
              <li
                className={activeTab === '2025' ? 'active' : ''}
                onClick={() => setActiveTab('2025')}
              >
                2025
              </li>
              <li
                className={activeTab === '2020' ? 'active' : ''}
                onClick={() => setActiveTab('2020')}
              >
                2020 *
              </li>
            </ul>
          </div>
          <div className="tab-container">
            <div className="tab-content">
              {isLoading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <ElectionChart
                    endpoint={currentDistrictEndpoint}
                    seriesConfig={seriesConfig}
                    plotOptions={plotOptions}
                    title=""
                    height="220"
                    showOnlyHighest={true}
                    activeTab={activeTab}
                  />
                  <ElectionResultsTable
                    endpoint={currentDistrictEndpoint}
                    showParty2020={true}
                    dataKey="district"
                    fallbackData={FALLBACK_DATA}
                    showHung={activeTab === '2025'}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        <ActionButtons
          onPartyWise={handlePartyWise}
          onMapView={handleMapView}
        />
      </div>
      {shareState?.showModal && (
        <ShareModal
          isOpen={shareState.showModal}
          onClose={() => setShareState(null)}
          shareLinks={shareState.shareLinks}
          shareText={shareState.shareText}
          url={shareState.url}
        />
      )}
    </>
  );
};

export default DistrictWise;